/**
 * resolveCellQueueWindowContext — derives the `useCellQueueJoin` window
 * context (mediaUnitId/dateFrom/dateTo/enabled/ownership) from a cell's
 * `CellEntry[]` and period (CU-86cb7ewja). Shared by the inventory catalog
 * and the media-unit detail calendar so each route's open-cell tracking only
 * needs to supply its own (already-open) entries/period/mediaUnitId — the
 * "is this an active hold, and whose" resolution lives in one place.
 */
import type { CellEntry } from '@ooh/contracts';
import { resolveDominantEntry } from '../../availability/components/inspect-popover-parts';

export interface CellQueueWindowContextLike {
  mediaUnitId: number;
  dateFrom: string;
  dateTo: string;
  enabled: boolean;
  ownership: 'other' | 'mine';
}

/** The shared "no active hold" sentinel every `CellQueueWindowContext`-shaped resolver
 * (this file's own INSPECT-side resolution and `resolveSelectQueueContext`'s SELECT-side
 * one) falls back to — kept in ONE place so both resolvers agree on its exact shape. */
export const INACTIVE_CONTEXT: CellQueueWindowContextLike = {
  mediaUnitId: 0,
  dateFrom: '',
  dateTo: '',
  enabled: false,
  ownership: 'other',
};

export function resolveCellQueueWindowContext(
  entries: CellEntry[],
  period: { start: string; end: string } | undefined,
  mediaUnitId: number,
): CellQueueWindowContextLike {
  if (!period) return INACTIVE_CONTEXT;
  const { dominant } = resolveDominantEntry(entries);
  if (!dominant) return INACTIVE_CONTEXT;
  const isActiveHold =
    dominant.status === 'SOFT_RESERVATION' || dominant.status === 'HARD_RESERVATION';
  if (!isActiveHold) return INACTIVE_CONTEXT;
  return {
    mediaUnitId,
    dateFrom: period.start,
    dateTo: period.end,
    enabled: true,
    ownership: dominant.ownership === 'mine' ? 'mine' : 'other',
  };
}
